import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  Typography,
  useTheme,
} from '@mui/material';
import { useAppSelector } from '../../../../app/hooks';
import {
  CubeMeasureEnum,
  FeatureEnum,
} from '../../../../services/data/impl/cube/cubeSlice';
import { useTranslation } from '../../../../services/translation/hooks/useTranslation';
import { flexCenter } from '../../../commonUI/cssClasses';
import LoadingSpinner from '../../../commonUI/LoadingSpinner';
import {
  getUnitSymbol,
  MeasurementTypes,
  useUnitSymbol,
} from '../../../commonUtils';
import { getHeatmapColorsDistributionByRawData } from '../../../map/mapUtils';
import { selectIsLoadingCustomerSummary } from '../../../ranches/ranchesSlice';
import { roundFractional } from '../../../ranches/ranchUtils';
import useScanData from '../../../scanDataApi/hooks/useScanData';
import {
  ScanDataStatusEnum,
  selectCurrFeature,
  selectCurrMeasure,
  selectScanDataStatus,
} from '../../../select/selectSlice';
import { useUnits } from '../../../settings/hooks/useUnits';
import { selectPointsHeatmapLoading } from '../../mapSlice';
import { ColorBox } from './components/ColorBox';

const HeatmapLegend: React.FC = () => {
  const theme = useTheme();
  const units = useUnits();
  const { standardDeviation, mean, scanData } = useScanData();
  const ranges =
    !!standardDeviation.data && !!mean.data
      ? getHeatmapColorsDistributionByRawData(standardDeviation.data, mean.data)
      : null;
  const currFeature = useAppSelector(selectCurrFeature);
  const currMeasure = useAppSelector(selectCurrMeasure);
  const scanDataStatus = useAppSelector(selectScanDataStatus);
  const pointsHeatmapLoading = useAppSelector(selectPointsHeatmapLoading);
  const isLoadingCustomerSummary = useAppSelector(
    selectIsLoadingCustomerSummary
  );
  const meterOrFeetUnitSymbol = useUnitSymbol(MeasurementTypes.meterOrFeet);
  const millimeterUnitSymbol = useUnitSymbol(MeasurementTypes.millimeterOrInch);

  const { t } = useTranslation();

  if (
    standardDeviation.isLoading ||
    mean.isLoading ||
    scanData.isLoading ||
    scanDataStatus === ScanDataStatusEnum.loading ||
    pointsHeatmapLoading ||
    isLoadingCustomerSummary
  ) {
    return (
      <Box
        sx={{
          width: '100%',
          ...flexCenter,
          flexDirection: 'column',
          p: '0.3rem',
        }}
      >
        <LoadingSpinner />
      </Box>
    );
  }

  if (!ranges) {
    return (
      <Box
        sx={{
          width: '100%',
          ...flexCenter,
          flexDirection: 'column',
          p: '0.3rem',
        }}
      >
        <Typography sx={{ color: (theme) => theme.colors.common.white }}>
          {t('No Data', { ns: 'common' })}
        </Typography>
      </Box>
    );
  }

  let title = `${t(currFeature, { ns: 'common' })} / ${meterOrFeetUnitSymbol}`; // Count measure is default
  if (currMeasure === CubeMeasureEnum.DetectionFactAvgLength) {
    title = `${t(CubeMeasureEnum.DetectionFactAvgLength, {
      ns: 'common',
    })} (${getUnitSymbol(units, MeasurementTypes.millimeterOrInch, t)})`;

    if (currFeature === FeatureEnum.berries) {
      title = `${t('Average', { ns: 'common' })} ${t('Diameter', {
        ns: 'common',
      })} (${millimeterUnitSymbol})`;
    } else if (
      [FeatureEnum.shoot, FeatureEnum.earlyShoot].includes(currFeature)
    ) {
      title = `${t('Length', { ns: 'common' })} (${getUnitSymbol(
        units,
        MeasurementTypes.millimeterOrInch,
        t
      )})`;
    } else {
      title = `${t(currMeasure, { ns: 'common' })} (${getUnitSymbol(
        units,
        MeasurementTypes.millimeterOrInch,
        t
      )})`;
    }
  }

  return (
    <Box
      sx={{
        width: '100%',
        ...flexCenter,
        flexDirection: 'column',
        p: '0.3rem',
      }}
    >
      <Typography
        sx={{
          fontSize: '1rem',
          color: theme.colors.common.white,
        }}
        gutterBottom
      >
        {title}
      </Typography>
      <List dense sx={{ p: 0 }}>
        {ranges.map((el, idx) => (
          <ListItem key={idx} sx={{ p: 0 }}>
            <ListItemIcon sx={{ minWidth: '2rem' }}>
              <ColorBox color={el[2] as string} />
            </ListItemIcon>
            <Typography
              sx={{
                fontSize: '1rem',
                color: theme.colors.common.white,
              }}
            >
              {idx !== ranges.length - 1
                ? `${roundFractional(el[0], 1)} - ${roundFractional(el[1], 1)}`
                : `> ${roundFractional(el[0], 1)}`}
            </Typography>
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

export default HeatmapLegend;
